import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import servicesData from "../../../../data/servicesData";
import LanguageToggle from "../LanguageToggle/LanguageToggle";
import { useContentAvailability } from "../../../../utils/contentAvailability";

// Map servicesData to the format expected by the header
const servicesLinkData = servicesData.map((service) => ({
  linkLabel: service.title,
  pageLink: service.link,
}));

const MobileHeader = () => {
  const { hasBlogs, hasCaseStudies } = useContentAvailability();
  const [isNavActive, setIsNavActive] = useState(false);
  const [activeSubNav, setActiveSubNav] = useState(null);
  const navRef = useRef(null);
  const overlayRef = useRef(null);

  // Filter resources based on availability
  const resourcesLinkData = [
    hasBlogs && {
      linkLabel: "Blog",
      pageLink: "/blogs",
    },
    hasCaseStudies && {
      linkLabel: "Case Studies",
      pageLink: "/case-studies",
    },
  ].filter(Boolean);

  const toggleNav = () => {
    setIsNavActive((prev) => !prev);
  };

  const toggleSubNav = (index) => {
    setActiveSubNav((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const handleOverlayClick = () => {
      setIsNavActive(false);
    };

    const overlayElement = overlayRef.current;
    if (overlayElement) {
      overlayElement.addEventListener("click", handleOverlayClick);
    }

    return () => {
      if (overlayElement) {
        overlayElement.removeEventListener("click", handleOverlayClick);
      }
    };
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      setIsNavActive(false);
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  return (
    <header className="relative flex flex-col xl:hidden">
      <div className="h-16 bg-[#0A0118] flex items-center justify-between fixed top-0 left-0 z-50 w-full px-4">
        <Link to="/" aria-label="logo">
          <img src="/assets/images/logo.svg" alt="logo" className="h-8" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="scale-75 origin-right">
            <LanguageToggle />
          </div>
          <button
            aria-label="mobile-Menu"
            className="w-6 h-6 text-white toggle_nav_menu"
            id="mobile_nav_toggle_menu"
            onClick={toggleNav}
          >
            <svg
              className={`transition-all duration-300 pointer-events-none ${isNavActive ? "hidden" : ""
                }`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            <svg
              className={`transition-all duration-300 pointer-events-none ${isNavActive ? "" : "hidden"
                }`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      <div className="transition-all duration-300">
        <div
          className={`fixed z-40 w-full h-screen transition-all duration-300 delay-150 bg-black/80 ${isNavActive ? "left-0" : "-left-full"
            }`}
          id="mobile-nav-div-overlay"
          ref={overlayRef}
        ></div>
        <div
          className={`fixed z-50 w-full h-screen overflow-y-scroll transition-all duration-300 bg-[#0A0118] top-16 ${isNavActive ? "left-0" : "-left-full"
            }`}
          id="mobile-nav-div"
          ref={navRef}
        >
          <div className="flex flex-col pt-8 px-6 gap-8">
            {/* Home */}
            <div className="group">
              <Link
                to="/"
                className="text-white font-semibold hover:text-purple home-two-nav-item leading-5 relative transition-all ease-out duration-300 w-fit flex items-center"
                onClick={() => setIsNavActive(false)}
              >
                Home
              </Link>
            </div>

            {/* Services Dropdown */}
            <div className="group">
              <div className="flex items-center gap-3">
                <Link
                  to="/services"
                  className="text-white font-semibold hover:text-purple home-two-nav-item leading-5 relative transition-all ease-out duration-300 w-fit"
                  onClick={() => setIsNavActive(false)}
                >
                  Services
                </Link>
                <button
                  onClick={() => toggleSubNav(0)}
                  className="text-white hover:text-purple transition-all duration-300 p-1"
                  aria-label="Toggle services submenu"
                >
                  <svg
                    className={`transition-all duration-300 ${activeSubNav === 0 ? "rotate-180" : ""
                      }`}
                    width="10"
                    height="10"
                    viewBox="0 0 19 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 2L9.5 8L17 2"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div
                className={`overflow-hidden transition-all duration-300 ${activeSubNav === 0 ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
              >
                <ul className="pt-4 pl-4 space-y-3">
                  {servicesLinkData.map((item, index) => (
                    <li key={index} className="relative">
                      <Link
                        className="relative leading-5 text-white/80 home-two-nav-item hover:text-purple w-fit"
                        to={item.pageLink}
                        onClick={() => setIsNavActive(false)}
                      >
                        {item.linkLabel}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Industries */}
            <div className="group">
              <Link
                className="text-white font-semibold hover:text-purple home-two-nav-item leading-5 relative transition-all ease-out duration-300 w-fit"
                to="/industries"
                onClick={() => setIsNavActive(false)}
              >
                Industries
              </Link>
            </div>

            {/* About */}
            <div className="group">
              <Link
                className="text-white font-semibold hover:text-purple home-two-nav-item leading-5 relative transition-all ease-out duration-300 w-fit"
                to="/aboutus"
                onClick={() => setIsNavActive(false)}
              >
                About
              </Link>
            </div>

            {/* Resources Dropdown */}
            {resourcesLinkData.length > 0 && (
              <div className="group">
                <button
                  className="text-white font-semibold hover:text-purple home-two-nav-item leading-5 relative transition-all ease-out duration-300 w-fit flex items-center gap-2"
                  onClick={() => toggleSubNav(1)}
                >
                  Resources
                  <svg
                    className={`transition-all duration-300 ${activeSubNav === 1 ? "rotate-180" : ""
                      }`}
                    width="10"
                    height="10"
                    viewBox="0 0 19 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 2L9.5 8L17 2"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${activeSubNav === 1 ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                  <ul className="pt-4 pl-4 space-y-3">
                    {resourcesLinkData.map((item, index) => (
                      <li key={index} className="relative">
                        <Link
                          className="relative leading-5 text-white/80 home-two-nav-item hover:text-purple w-fit"
                          to={item.pageLink}
                          onClick={() => setIsNavActive(false)}
                        >
                          {item.linkLabel}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* CTA Button */}
          <div className="px-6 mt-8">
            <Link to="/contact" onClick={() => setIsNavActive(false)}>
              <div className="home-two-btn-bg py-2.5 group bg-purple border-purple w-fit">
                <span className="relative z-10 text-base font-semibold text-white transition-all duration-300 group-hover:text-purple font-inter">
                  Lets Connect
                </span>
                <svg
                  className="relative z-10"
                  width="7"
                  height="12"
                  viewBox="0 0 7 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="transition-all duration-300 group-hover:stroke-purple"
                    d="M1.10254 10.5L4.89543 6.70711C5.22877 6.37377 5.39543 6.20711 5.39543 6C5.39543 5.79289 5.22877 5.62623 4.89543 5.29289L1.10254 1.5"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
